/**
 * @file SSOList.js
 * @description This component displays the SSO list in either Multitenant Portal or Client Portal mode with filtering functionality.
 */

import React, {
    useState,
    useEffect,
    useContext,
    useRef,
    useCallback,
    useMemo,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../store/AuthContext';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import styles from './sso.module.scss';
import '../page-list/PageList.css';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import {
    FilterModal,
    useFilter,
    renderFilterIcon,
} from '../helpers/FilterComponent';
import {
    fetchSystemSSOList,
    fetchClientSSOList,
    deleteSystemSSO,
    deleteClientSSO,
    updateSystemSSO,
    updateClientSSO,
    createSystemSSO,
    fetchSystemSSOById,
    fetchClientSSOById,
    fetchSystemSSOClients,
} from '../../services/ssoService';
import {
    formatDate,
    formatName,
    getNameForDuplicate,
    isValidUrl,
} from '../../utils/utils';
import {
    closePendingWindow,
    navigatePendingWindow,
    openPendingWindow,
} from '../../utils/pendingWindow';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import AlertBox from '../ui/no_data_indicators/AlertBox';
import { LogEventType, SecurityAttributes } from '../../utils/data/enums';
import { logEvent } from '../../services/loggingService';
import { getText } from '../../i18n';

const SortDirections = {
    ASCENDING: 'asc',
    DESCENDING: 'desc',
};

const SSOList = () => {
    const navigate = useNavigate();
    const { showModal } = useContext(ModalContext);
    const { user, multitenantFlag, hasAttribute } = useAuthContext();
    const [isLoading, setIsLoading] = useState(true);
    const [ssoList, setSSOList] = useState([]);
    const [selectedSSOs, setSelectedSSOs] = useState([]);
    const [testingSSOId, setTestingSSOId] = useState(null);
    const [isUnauthorized, setUnauthorized] = useState(false);
    const [sortConfig, setSortConfig] = useState({
        key: 'name',
        direction: SortDirections.ASCENDING,
    });
    const [activeModal, setActiveModal] = useState(null);
    const [selections, setSelections] = useState({});
    const [baseFilteredData, setBaseFilteredData] = useState([]);
    const [filteredData, applyFilter, clearAllFilters] =
        useFilter(baseFilteredData);
    const [searchTerm, setSearchTerm] = useState('');

    // default to read only
    let isReadOnly = true;

    // System SSOs
    if (multitenantFlag && hasAttribute(SecurityAttributes.GlobalSSOFullAccess))
        isReadOnly = false;
    // Client SSOs
    else if (
        !multitenantFlag &&
        hasAttribute(SecurityAttributes.ClientSSOFullAccess)
    )
        isReadOnly = false;

    const selectAllRef = useRef(null);

    useEffect(() => {
        loadSSOList();
    }, [multitenantFlag]);

    const loadSSOList = async () => {
        try {
            setIsLoading(true);
            const fetchedSSOList = multitenantFlag
                ? await fetchSystemSSOList()
                : await fetchClientSSOList();

            const formattedSSOList = fetchedSSOList.map(formatSSOData);
            setSSOList(formattedSSOList);
            return fetchedSSOList;
        } catch (error) {
            if (error.status === 400 || error.status === 401) {
                setUnauthorized(true);
            } else {
                console.error('Failed to fetch SSO list', error);
                Toast.error(getText('msg_36cb05bc955a'));
            }
        } finally {
            setIsLoading(false);
        }
    };

    const openModal = column => {
        setActiveModal(column);
    };

    const closeModal = () => {
        setActiveModal(null);
    };

    const handleApply = (column, selectedOptions) => {
        setSelections(prev => ({ ...prev, [column]: selectedOptions }));
        applyFilter(column, selectedOptions);
    };

    const clearFilters = () => {
        setSelections({});
        setSearchTerm('');
        clearAllFilters();
    };

    const getOptions = column => {
        const filtersWithoutCurrent = { ...selections };
        delete filtersWithoutCurrent[column];

        const filteredData = baseFilteredData.filter(sso =>
            Object.entries(filtersWithoutCurrent).every(
                ([key, values]) =>
                    values.length === 0 || values.includes(sso[key]),
            ),
        );

        return Array.from(new Set(filteredData.map(item => item[column])))
            .filter(Boolean) // Remove null/undefined values
            .sort((a, b) => {
                if (typeof a === 'string' && typeof b === 'string') {
                    return a.localeCompare(b);
                }
                return a - b;
            });
    };

    const handleSearch = event => {
        setSearchTerm(event.target.value);
    };

    useEffect(() => {
        const searchFiltered = ssoList.filter(
            sso =>
                sso.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                sso.initiatedBy
                    ?.toLowerCase()
                    .includes(searchTerm.toLowerCase()) ||
                sso.vendorEnvironment
                    ?.toLowerCase()
                    .includes(searchTerm.toLowerCase()),
        );
        setBaseFilteredData(searchFiltered);
    }, [searchTerm, ssoList]);

    const filteredSSOs = useMemo(() => {
        return ssoList.filter(
            sso =>
                sso.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                sso.initiatedBy
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase()),
        );
    }, [ssoList, searchTerm]);

    const sortedSSOList = useMemo(() => {
        return [...filteredData].sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            if (!aValue) aValue = '';
            if (!bValue) bValue = '';

            if (typeof aValue === 'string') aValue = aValue.toLowerCase();
            if (typeof bValue === 'string') bValue = bValue.toLowerCase();

            if (aValue < bValue) {
                return sortConfig.direction === SortDirections.ASCENDING
                    ? -1
                    : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === SortDirections.ASCENDING
                    ? 1
                    : -1;
            }
            return 0;
        });
    }, [filteredData, sortConfig]);

    const handleSort = key => {
        setSortConfig(prevConfig => ({
            key,
            direction:
                prevConfig.key === key &&
                prevConfig.direction === SortDirections.ASCENDING
                    ? SortDirections.DESCENDING
                    : SortDirections.ASCENDING,
        }));
    };

    const handleDelete = async () => {
        if (isReadOnly) {
            Toast.error(getText('msg_710b08013fdd'));
            return;
        }

        let deletedCount = 0;
        const errors = [];

        for (const ssoId of selectedSSOs) {
            try {
                if (multitenantFlag) {
                    await deleteSystemSSO(ssoId);
                } else {
                    await deleteClientSSO(ssoId);
                }
                deletedCount++;
            } catch (error) {
                console.error(`Failed to delete SSO with ID ${ssoId}:`, error);
                errors.push(ssoId);
            }
        }

        if (deletedCount > 0) {
            await loadSSOList();
        }

        if (deletedCount === selectedSSOs.length) {
            Toast.success(`${deletedCount} SSO item(s) deleted successfully`);
        } else if (deletedCount > 0) {
            Toast.warning(
                `${deletedCount} out of ${selectedSSOs.length} SSO item(s) deleted successfully. Some deletions failed.`,
            );
        } else {
            Toast.error(getText('msg_e9bbae528a63'));
        }

        setSelectedSSOs([]);
    };

    const handleDuplicate = async () => {
        if (!multitenantFlag) {
            Toast.error(getText('msg_2db438cc450b'));
            return;
        }

        if (selectedSSOs.length !== 1) {
            Toast.error(getText('msg_85f87a1da8ab'));
            return;
        }

        try {
            const [ssoId] = selectedSSOs;
            const ssoToDuplicate = await fetchSystemSSOById(ssoId);

            if (!ssoToDuplicate) {
                throw new Error('Selected SSO not found');
            }

            const ssoNames = ssoList.map(sso => sso.name);
            const newName = getNameForDuplicate(ssoToDuplicate.name, ssoNames);

            const newSSO = {
                ...ssoToDuplicate,
                id: 0,
                name: newName,
                ssoAttributes: ssoToDuplicate.ssoAttributes?.map(attribute => ({
                    ...attribute,
                    id: 0,
                })),
            };

            await createSystemSSO(newSSO);
            await loadSSOList();
            Toast.success(getText('msg_928792a0a24c'));
            setSelectedSSOs([]);
        } catch (error) {
            console.error('Failed to duplicate SSO', error);
            Toast.error(getText('msg_63665f85866b'));
        }
    };

    const handleToggleSSOStatus = async ssoId => {
        if (isReadOnly) {
            Toast.error(getText('msg_61093dce661b'));
            return;
        }

        try {
            const sso = await (multitenantFlag
                ? fetchSystemSSOById(ssoId)
                : fetchClientSSOById(ssoId));

            if (!sso) throw new Error('SSO not found');

            if (sso.isActive) {
                if (multitenantFlag) {
                    try {
                        const clients = await fetchSystemSSOClients(ssoId);
                        const hasLiveClients = clients.some(
                            client => client.status === 'Live',
                        );

                        if (hasLiveClients) {
                            showModal({
                                type: ModalTypes.ACTION,
                                title: getText('msg_92c103804f84'),
                                text: getText('msg_947f3d71ed49'),
                                onYes: async () => {
                                    const updatedSSO = {
                                        ...sso,
                                        isActive: false,
                                    };
                                    await updateSystemSSO(updatedSSO);
                                    await loadSSOList();
                                    Toast.success(getText('msg_1eb469c4fa32'));
                                },
                                onNo: () => {},
                                noButtonText: getText('msg_77dfd2135f4d'),
                                yesButtonText: getText('msg_9a7d4e0687b1'),
                            });
                            return;
                        }
                    } catch (error) {
                        console.error(
                            'Error checking for live clients:',
                            error,
                        );
                        Toast.error(getText('msg_77f999aff0d6'));
                        return;
                    }
                } else {
                    const ssoItem = ssoList.find(item => item.id === ssoId);
                    const isLive = `${ssoItem?.status}`
                        .toLowerCase()
                        .includes('live');

                    if (isLive) {
                        showModal({
                            type: ModalTypes.ACTION,
                            title: getText('msg_92c103804f84'),
                            text: getText('msg_408bc7e21e09'),
                            onYes: async () => {
                                const updatedSSO = {
                                    ...sso,
                                    isActive: false,
                                    tenantSSOStatusId: 2,
                                };
                                await updateClientSSO(updatedSSO);
                                await loadSSOList();
                                Toast.success(getText('msg_1eb469c4fa32'));
                            },
                            onNo: () => {},
                            noButtonText: getText('msg_77dfd2135f4d'),
                            yesButtonText: getText('msg_9a7d4e0687b1'),
                        });
                        return;
                    }
                }
            }

            const updatedSSO = {
                ...sso,
                isActive: !sso.isActive,
            };

            if (!multitenantFlag && !updatedSSO.isActive) {
                updatedSSO.tenantSSOStatusId = 2;
            }

            await (multitenantFlag
                ? updateSystemSSO(updatedSSO)
                : updateClientSSO(updatedSSO));

            await loadSSOList();
            Toast.success(
                `SSO ${updatedSSO.isActive ? 'enabled' : 'disabled'} successfully`,
            );
        } catch (error) {
            console.error('Failed to toggle SSO status', error);
            Toast.error(getText('msg_df2874231e47'));
        }
    };
    const handleSSOSelection = ssoId => {
        if (isReadOnly) {
            return;
        }

        setSelectedSSOs(prevSelection =>
            prevSelection.includes(ssoId)
                ? prevSelection.filter(id => id !== ssoId)
                : [...prevSelection, ssoId],
        );
    };

    const handleSelectAllChange = event => {
        if (isReadOnly) {
            return;
        }

        setSelectedSSOs(event.target.checked ? ssoList.map(sso => sso.id) : []);
    };

    const handleViewSSO = id => {
        const path = multitenantFlag
            ? `/sso-multi/view/${id}`
            : `/sso-client/view/${id}`;
        navigate(path);
    };

    const handleAddSSO = () => {
        if (isReadOnly) {
            Toast.error(getText('msg_68770bd39152'));
            return;
        }

        const path = multitenantFlag ? '/sso-multi/add' : '/sso-client/add';
        navigate(path);
    };

    const handleTestSSO = async ssoId => {
        setTestingSSOId(ssoId);
        const pendingWindow = openPendingWindow();
        try {
            let ssoData = await fetchClientSSOById(ssoId);

            if (!isValidUrl(ssoData?.url)) {
                ssoData = await fetchSystemSSOById(ssoData.ssoId);
            }

            if (isValidUrl(ssoData?.url)) {
                navigatePendingWindow(pendingWindow, ssoData.url);
            } else {
                closePendingWindow(pendingWindow);
                Toast.error(getText('msg_d7e8f9b00a50'));
            }
        } catch (error) {
            closePendingWindow(pendingWindow);
            console.error('Error testing SSO:', error);
            Toast.error(getText('msg_d8bbfcbfb16a'));
        } finally {
            setTestingSSOId(null);
        }
    };

    const openDeleteConfirmationModal = async () => {
        if (!selectedSSOs.length) {
            Toast.error(getText('msg_9bd1b9956da8'));
            return;
        }

        if (multitenantFlag) {
            try {
                const ssoStatuses = await Promise.all(
                    selectedSSOs.map(async ssoId => {
                        const clients = await fetchSystemSSOClients(ssoId);
                        return clients.some(client => client.status === 'Live');
                    }),
                );

                const hasLiveClients = ssoStatuses.some(isActive => isActive);
                const totalSelected = selectedSSOs.length;
                const activeCount = ssoStatuses.filter(
                    isActive => isActive,
                ).length;

                let title = getText('msg_b78130c92f32');
                let message;
                let subtitle;
                if (hasLiveClients) {
                    if (activeCount === totalSelected) {
                        subtitle = 'This SSO is currently active.';
                        message = `You must remove the SSO(s) from live content in order to complete this action.`;
                    } else {
                        subtitle =
                            'One or more of the selected SSO(s) are currently in use.';
                        message = `You must remove the active SSO(s) from live content in order to complete this action.`;
                    }
                } else {
                    message =
                        'This action will delete the selected SSO(s) and remove them from all references on the site.';
                }

                if (!hasLiveClients) {
                    showDeleteConfirmationModal();
                    return;
                }

                showModal({
                    type: ModalTypes.ACTION,
                    title: title ? title : '',
                    subtitle: subtitle,
                    text: message,
                    onYes: () => {
                        Toast.info(getText('msg_f46f13de3bc0'));
                    },
                    onNo: () => {},
                    noButtonText: getText('msg_77dfd2135f4d'),
                    yesButtonText: hasLiveClients ? 'Ok' : 'Continue',
                });
            } catch (error) {
                console.error('Error checking for live clients:', error);
                Toast.error(getText('msg_77f999aff0d6'));
            }
        } else {
            // Client-side logic
            const selectedSSOItems = ssoList.filter(sso =>
                selectedSSOs.includes(sso.id),
            );
            const activeSSOCount = selectedSSOItems.filter(sso =>
                `${sso?.status}`.toLowerCase().includes('live'),
            ).length;

            if (activeSSOCount > 0) {
                let title = getText('msg_b78130c92f32');
                let message;
                let subtitle;

                if (activeSSOCount === selectedSSOItems.length) {
                    subtitle =
                        selectedSSOItems.length === 1
                            ? 'This SSO is currently in use.'
                            : 'These SSOs are currently in use.';
                    message = `You must remove the SSO${
                        selectedSSOItems.length > 1 ? 's' : ''
                    } from live content in order to complete this action.`;
                } else {
                    subtitle =
                        'One or more of the selected SSO(s) are currently in use.';
                    message =
                        'You must remove the active SSO(s) from live content in order to complete this action.';
                }

                showModal({
                    type: ModalTypes.ACTION,
                    title: title,
                    subtitle: subtitle,
                    text: message,
                    onYes: () => {
                        Toast.info(getText('msg_f46f13de3bc0'));
                    },
                    onNo: () => {},
                    noButtonText: getText('msg_77dfd2135f4d'),
                    yesButtonText: getText('msg_b0a98216a324'),
                });
            } else {
                showDeleteConfirmationModal();
            }
        }
    };

    const showDeleteConfirmationModal = () => {
        setTimeout(() => {
            showModal({
                type: ModalTypes.DELETE_CONFIRMATION,
                title: getText('msg_b957efeb6067'),
                text: `This action will delete ${
                    selectedSSOs?.length >= 2
                        ? 'these SSO(s) and remove them'
                        : 'this SSO and remove it'
                } from all global references.`,
                onYes: () => handleDelete(),
                onNo: () => {},
                noButtonText: getText('msg_77dfd2135f4d'),
                yesButtonText: getText('msg_f6fdbe48dc54'),
            });
            logEvent(LogEventType.DeleteClicked, `SSO delete clicked`, null);
        });
    };

    const openDuplicateConfirmationModal = () => {
        if (selectedSSOs.length !== 1) {
            Toast.error(getText('msg_85f87a1da8ab'));
            return;
        }
        handleDuplicate();
    };

    const formatSSOData = sso => {
        return {
            ...sso,
            lastEdited: formatDate(sso.lastEdited),
            editedBy: formatName(sso.editedBy),
        };
    };
    const renderSortArrow = key => {
        if (sortConfig.key !== key) {
            return null;
        }
        return (
            <span className={`mx-2 ${styles.sortArrowIcon}`}>
                <svg width="11" height="6" viewBox="0 0 11 6" fill="none">
                    <path
                        d={
                            sortConfig.direction === SortDirections.ASCENDING
                                ? 'M1 5.25L5.25 0.75L9.5 5.25'
                                : 'M1 0.75L5.25 5.25L9.5 0.75'
                        }
                        stroke="#2F2B43"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </span>
        );
    };

    const renderMultitenantColumns = () => (
        <>
            <th className={`text-center checkbox-column-head`}>
                <input
                    type="checkbox"
                    className={`form-check-input`}
                    ref={selectAllRef}
                    checked={
                        selectedSSOs.length === ssoList.length &&
                        ssoList.length > 0
                    }
                    onChange={handleSelectAllChange}
                    disabled={isReadOnly}
                />
            </th>
            <th
                style={{ cursor: 'pointer' }}
                className="position-relative"
                onClick={() => handleSort('name')}
            >
                <span className="table-column-header-text">
                    {getText('msg_709a23220f2c')} {renderSortArrow('name')}
                </span>
                <span
                    onClick={e => {
                        e.stopPropagation();
                        openModal('name');
                    }}
                    className={`${styles.cursorPointer} pe-3`}
                >
                    {renderFilterIcon(selections['name']?.length > 0)}
                </span>
                {activeModal === 'name' && (
                    <FilterModal
                        column={activeModal}
                        options={getOptions(activeModal)}
                        selectedOptions={selections[activeModal] || []}
                        onApply={handleApply}
                        onClose={closeModal}
                    />
                )}
            </th>
            <th onClick={() => handleSort('initiatedBy')}>
                <span className="table-column-header-text">
                    {getText('msg_2d0ad15e4439')}{' '}
                    {renderSortArrow('initiatedBy')}
                </span>
                <span
                    onClick={e => {
                        e.stopPropagation();
                        openModal('initiatedBy');
                    }}
                    className={styles.cursorPointer}
                >
                    {renderFilterIcon(selections['initiatedBy']?.length > 0)}
                </span>
                {activeModal === 'initiatedBy' && (
                    <FilterModal
                        column={activeModal}
                        options={getOptions(activeModal)}
                        selectedOptions={selections[activeModal] || []}
                        onApply={handleApply}
                        onClose={closeModal}
                    />
                )}
            </th>
            <th onClick={() => handleSort('vendorEnvironment')}>
                <span className="table-column-header-text">
                    {getText('msg_13f283d69156')}{' '}
                    {renderSortArrow('vendorEnvironment')}
                </span>
                <span
                    onClick={e => {
                        e.stopPropagation();
                        openModal('vendorEnvironment');
                    }}
                    className={styles.cursorPointer}
                >
                    {renderFilterIcon(
                        selections['vendorEnvironment']?.length > 0,
                    )}
                </span>
                {activeModal === 'vendorEnvironment' && (
                    <FilterModal
                        column={activeModal}
                        options={getOptions(activeModal)}
                        selectedOptions={selections[activeModal] || []}
                        onApply={handleApply}
                        onClose={closeModal}
                    />
                )}
            </th>
            <th onClick={() => handleSort('clientCount')}>
                <span className="table-column-header-text">
                    {getText('msg_28e22fe3dde5')}{' '}
                    {renderSortArrow('clientCount')}
                </span>
            </th>
            <th onClick={() => handleSort('lastEdited')}>
                <span className="table-column-header-text">
                    {getText('msg_0c79adcf41ec')}{' '}
                    {renderSortArrow('lastEdited')}
                </span>
            </th>
            <th onClick={() => handleSort('editedBy')}>
                <span className="table-column-header-text">
                    {getText('msg_6180d5db3ed9')} {renderSortArrow('editedBy')}
                </span>
                <span
                    onClick={e => {
                        e.stopPropagation();
                        openModal('editedBy');
                    }}
                    className={styles.cursorPointer}
                >
                    {renderFilterIcon(selections['editedBy']?.length > 0)}
                </span>
                {activeModal === 'editedBy' && (
                    <FilterModal
                        column={activeModal}
                        options={getOptions(activeModal)}
                        selectedOptions={selections[activeModal] || []}
                        onApply={handleApply}
                        onClose={closeModal}
                    />
                )}
            </th>
            <th>
                <span className="table-column-header-text">
                    {getText('msg_20063ad90532')}
                </span>
            </th>
        </>
    );

    const renderClientColumns = () => (
        <>
            <th className={`text-center checkbox-column-head`}>
                <input
                    type="checkbox"
                    className={`form-check-input`}
                    ref={selectAllRef}
                    checked={
                        selectedSSOs.length === ssoList.length &&
                        ssoList.length > 0
                    }
                    onChange={handleSelectAllChange}
                />
            </th>
            <th
                style={{ cursor: 'pointer' }}
                className="position-relative"
                onClick={() => handleSort('name')}
            >
                <span className="table-column-header-text">
                    {getText('msg_709a23220f2c')} {renderSortArrow('name')}
                </span>
                <span
                    onClick={e => {
                        e.stopPropagation();
                        openModal('name');
                    }}
                    className={styles.cursorPointer}
                >
                    {renderFilterIcon(selections['name']?.length > 0)}
                </span>
                {activeModal === 'name' && (
                    <FilterModal
                        column={activeModal}
                        options={getOptions(activeModal)}
                        selectedOptions={selections[activeModal] || []}
                        onApply={handleApply}
                        onClose={closeModal}
                    />
                )}
            </th>
            <th onClick={() => handleSort('initiatedBy')}>
                <span className="table-column-header-text">
                    {getText('msg_2d0ad15e4439')}{' '}
                    {renderSortArrow('initiatedBy')}
                </span>
                <span
                    onClick={e => {
                        e.stopPropagation();
                        openModal('initiatedBy');
                    }}
                    className={styles.cursorPointer}
                >
                    {renderFilterIcon(selections['initiatedBy']?.length > 0)}
                </span>
                {activeModal === 'initiatedBy' && (
                    <FilterModal
                        column={activeModal}
                        options={getOptions(activeModal)}
                        selectedOptions={selections[activeModal] || []}
                        onApply={handleApply}
                        onClose={closeModal}
                    />
                )}
            </th>
            <th onClick={() => handleSort('vendorEnvironment')}>
                <span className="table-column-header-text">
                    {getText('msg_13f283d69156')}{' '}
                    {renderSortArrow('vendorEnvironment')}
                </span>
                <span
                    onClick={e => {
                        e.stopPropagation();
                        openModal('vendorEnvironment');
                    }}
                    className={styles.cursorPointer}
                >
                    {renderFilterIcon(
                        selections['vendorEnvironment']?.length > 0,
                    )}
                </span>
                {activeModal === 'vendorEnvironment' && (
                    <FilterModal
                        column={activeModal}
                        options={getOptions(activeModal)}
                        selectedOptions={selections[activeModal] || []}
                        onApply={handleApply}
                        onClose={closeModal}
                    />
                )}
            </th>
            <th onClick={() => handleSort('status')}>
                <span className="table-column-header-text">
                    {getText('msg_bae7d5be7082')} {renderSortArrow('status')}
                </span>
                <span
                    onClick={e => {
                        e.stopPropagation();
                        openModal('status');
                    }}
                    className={styles.cursorPointer}
                >
                    {renderFilterIcon(selections['status']?.length > 0)}
                </span>
                {activeModal === 'status' && (
                    <FilterModal
                        column={activeModal}
                        options={getOptions(activeModal)}
                        selectedOptions={selections[activeModal] || []}
                        onApply={handleApply}
                        onClose={closeModal}
                    />
                )}
            </th>
            <th onClick={() => handleSort('lastEdited')}>
                <span className="table-column-header-text">
                    {getText('msg_0c79adcf41ec')}{' '}
                    {renderSortArrow('lastEdited')}
                </span>
            </th>
            <th onClick={() => handleSort('editedBy')}>
                <span className="table-column-header-text">
                    {getText('msg_6180d5db3ed9')} {renderSortArrow('editedBy')}
                </span>
                <span
                    onClick={e => {
                        e.stopPropagation();
                        openModal('editedBy');
                    }}
                    className={styles.cursorPointer}
                >
                    {renderFilterIcon(selections['editedBy']?.length > 0)}
                </span>
                {activeModal === 'editedBy' && (
                    <FilterModal
                        column={activeModal}
                        options={getOptions(activeModal)}
                        selectedOptions={selections[activeModal] || []}
                        onApply={handleApply}
                        onClose={closeModal}
                    />
                )}
            </th>
            <th>
                <span className="table-column-header-text">
                    {getText('msg_20063ad90532')}
                </span>
            </th>
            <th>
                <span className="table-column-header-text">
                    {getText('msg_640ab2bae07b')}
                </span>
            </th>
        </>
    );

    const getStatusClass = (status, isActive) => {
        if (!isActive) return 'ing-badge-disabled';
        const statusString = `${status}`.toLowerCase();
        switch (statusString) {
            case 'in progress':
                return 'ing-badge-pending';
            case 'testing':
                return 'ing-badge-inv-sent';
            case 'live':
                return 'ing-badge-active';
            case 'disabled':
                return 'ing-badge-disabled';
            default:
                return 'ing-badge-inactive';
        }
    };

    const renderMultitenantRow = sso => (
        <>
            <td className="text-center">
                <input
                    className="form-check-input"
                    type="checkbox"
                    checked={selectedSSOs.includes(sso.id)}
                    onChange={() => handleSSOSelection(sso.id)}
                    disabled={isReadOnly}
                />
            </td>
            <td
                className="pointer-cursor titleCell"
                onClick={() => handleViewSSO(sso.id)}
            >
                {sso.name}
            </td>
            <td className="subtitleCell">{sso.initiatedBy}</td>
            <td className="subtitleCell">{sso.vendorEnvironment}</td>
            <td className="subtitleCell">{sso.clientCount}</td>
            <td className="subtitleCell">{sso.lastEdited}</td>
            <td className="subtitleCell">{sso.editedBy}</td>
            <td>
                <div className="form-switch d-flex">
                    <input
                        className="form-check-input custom-switch-input ml-3"
                        type="checkbox"
                        checked={sso.isActive}
                        onChange={() => handleToggleSSOStatus(sso.id)}
                        disabled={isReadOnly}
                    />
                </div>
            </td>
        </>
    );

    const renderClientRow = sso => (
        <>
            <td className="text-center">
                <input
                    className="form-check-input"
                    type="checkbox"
                    checked={selectedSSOs.includes(sso.id)}
                    onChange={() => handleSSOSelection(sso.id)}
                />
            </td>
            <td className="titleCell" onClick={() => handleViewSSO(sso.id)}>
                {sso.name}
            </td>
            <td className="subtitleCell">{sso.initiatedBy}</td>
            <td className="subtitleCell">{sso.vendorEnvironment}</td>
            <td>
                <span className={`${getStatusClass(sso.status, sso.isActive)}`}>
                    {sso.status}
                </span>
            </td>
            <td className="subtitleCell">{sso.lastEdited}</td>
            <td className="subtitleCell">{sso.editedBy}</td>
            <td>
                <div className="form-switch">
                    <input
                        className="form-check-input custom-switch-input ml-3"
                        type="checkbox"
                        checked={sso.isActive}
                        onChange={() => handleToggleSSOStatus(sso.id)}
                        disabled={isReadOnly}
                    />
                </div>
            </td>
            <td className={styles.testURL}>
                {testingSSOId === sso.id ? (
                    <Spinner width={20} />
                ) : (
                    <i
                        className="bi bi-arrow-up-right-circle-fill cursor-pointer text-primary"
                        onClick={() => handleTestSSO(sso.id)}
                        disabled={testingSSOId === sso.id}
                    />
                )}
            </td>
        </>
    );

    return (
        <div className="page-content">
            {isUnauthorized ? (
                <AlertBox
                    title={getText('msg_53cc4f43d8a7')}
                    description={getText('msg_7de2a9f3cc6c')}
                />
            ) : (
                <div className="card">
                    <div className="card-body px-0 py-4">
                        <div className="d-flex justify-content-between align-items-center pb-3">
                            <div className="float-start d-flex align-items-center">
                                <p className="page-title card-title ms-4 me-5 mb-0">
                                    {getText('msg_bb014cef265f')}
                                </p>
                                <div className={styles.searchWrapper}>
                                    <input
                                        type="text"
                                        className={styles.searchInput}
                                        placeholder={getText(
                                            'msg_4c6641dbd337',
                                        )}
                                        value={searchTerm}
                                        onChange={handleSearch}
                                    />
                                    <span className={styles.searchIcon}>
                                        <i className="bi bi-search"></i>
                                    </span>
                                </div>
                            </div>
                            <div className="float-end d-flex align-items-center">
                                {!isReadOnly && (
                                    <span
                                        type="button"
                                        onClick={openDeleteConfirmationModal}
                                        className="ing-text-buttons mx-3"
                                    >
                                        <i className="bi bi-trash3 me-2" />
                                        {getText('msg_f6fdbe48dc54')}
                                    </span>
                                )}
                                {multitenantFlag && (
                                    <span
                                        type="button"
                                        onClick={openDuplicateConfirmationModal}
                                        className="ing-text-buttons mx-3"
                                    >
                                        <i className="bi bi-copy me-2" />
                                        {getText('msg_972d57379db3')}
                                    </span>
                                )}
                                <span
                                    type="button"
                                    onClick={clearFilters}
                                    className="ing-text-buttons mx-3"
                                >
                                    <i className="bi bi-arrow-counterclockwise me-2" />
                                    {getText('msg_381c4bf1d409')}
                                </span>
                                <span
                                    type="button"
                                    onClick={handleAddSSO}
                                    className="ms-3 me-4 button-base ing-button-primary-left align-middle"
                                    style={
                                        isReadOnly
                                            ? {
                                                  opacity: 0.5,
                                                  pointerEvents: 'none',
                                              }
                                            : undefined
                                    }
                                >
                                    <i className="bi bi-plus-lg me-1" />
                                    {getText('msg_61cc55aa0453')}
                                </span>
                            </div>
                        </div>

                        {isLoading ? (
                            <Spinner
                                width={50}
                                className={styles.loadingSpinner}
                            />
                        ) : ssoList.length === 0 ? (
                            <NotFoundBox
                                backgroundColor="#f0f4ff"
                                title={getText('msg_208f0d9b86b9')}
                                description={`There are no SSO configurations ${
                                    multitenantFlag
                                        ? 'in the multi-tenant portal'
                                        : 'for this client'
                                } at this time`}
                            />
                        ) : (
                            <div className="table-responsive-xxl w-100">
                                <table className="table  align-middle ing-table-striped">
                                    <thead className="custom-table-header table-light">
                                        <tr>
                                            {multitenantFlag
                                                ? renderMultitenantColumns()
                                                : renderClientColumns()}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedSSOList.map(sso => (
                                            <tr key={sso.id}>
                                                {multitenantFlag
                                                    ? renderMultitenantRow(sso)
                                                    : renderClientRow(sso)}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SSOList;
